import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { Download, Filter, User, Loader2, LayoutGrid, FileSpreadsheet, FileText, Calendar, Printer, FileType } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { TERM_OPTIONS, ASSESSMENT_TYPES } from "../../constants/assessments";
import StudentPerformanceModal from "../../components/StudentPerformanceModal";
import { useSearchParams } from "react-router-dom";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface ClassItem {
  id?: string;
  _id?: string;
  name: string;
}

interface SubjectItem {
  id?: string;
  _id?: string;
  name: string;
  short_name?: string | null;
}

interface StudentResult {
  studentId: string;
  studentName: string;
  results: {
    subjectId: string;
    subjectName: string;
    score: number;
    testTitle?: string;
    testDate?: string;
  }[];
}

const ensureArray = <T,>(value: unknown, fallbackKeys: string[] = []): T[] => {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (value && typeof value === "object") {
    for (const key of fallbackKeys) {
      const nestedValue = (value as Record<string, unknown>)[key];
      if (Array.isArray(nestedValue)) {
        return nestedValue as T[];
      }
    }
  }

  return [];
};

const formatAssessmentMetric = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "N/A";
  }
  return `${Math.round(value)}`;
};

const getAwardDisplay = (score: number | null | undefined) => {
  if (score === null || score === undefined || Number.isNaN(score)) {
    return "-";
  }
  if (score >= 80) return "GREEN";
  if (score >= 70) return "YELLOW";
  return "RED";
};

const abbreviateSubject = (name: string) => {
  const cleaned = name.trim().toUpperCase();
  if (cleaned === "CREATIVE AND TECHNOLOGY STUDIES") return "CTS";
  if (cleaned === "HOME ECONOMICS") return "HE";
  if (cleaned === "PHYSICAL EDUCATION") return "P.E.";
  if (cleaned === "SCIENCE PAPER 1") return "SP1";
  if (cleaned === "SCIENCE PAPER 2") return "SP2";
  return name;
};

const getSubjectLabel = (subject: SubjectItem) => {
  const shortName = typeof subject.short_name === "string" ? subject.short_name.trim() : "";
  return shortName || abbreviateSubject(subject.name);
};

const ViewTestResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [examType, setExamType] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [results, setResults] = useState<StudentResult[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [modalStudent, setModalStudent] = useState<{ id: string; name: string; email?: string; className?: string } | null>(null);

  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  // Assessment Type options (same as recordAssessments)
  const assessmentTypes = ASSESSMENT_TYPES;
  // Generate year options (current year ± 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString());
  // Fetch classes
  const fetchClasses = useCallback(async () => {
    if (!token) return;
    try {
      if (role === "admin") {
        const res = await axios.get(getApiUrl("academics/classes"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClasses(ensureArray<ClassItem>(res.data, ["classes", "data"]));
      } else if (role === "teacher") {
        const res = await axios.get(getApiUrl("teacher/dashboard"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const teacherClasses = ensureArray<ClassItem>(res.data?.classes, ["classes", "data"]);
        setClasses(teacherClasses);
      } else {
        setClasses([]);
      }
    } catch (err) {
      console.error("Error fetching classes:", err);
      setClasses([]);
    }
  }, [token, role]);
  // Fetch subjects for selected class
  const fetchSubjects = useCallback(async () => {
    if (!selectedClass || !token) {
      setSubjects([]);
      return;
    }
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${selectedClass}/subjects`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(ensureArray<SubjectItem>(res.data, ["subjects", "data"]));
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setSubjects([]);
    }
  }, [selectedClass, token]);
  // Fetch results
  const fetchResults = useCallback(async () => {
    if (!selectedClass || !selectedTerm || !examType || !selectedYear) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const url = `${getApiUrl(`assessments/results/class/${selectedClass}`)}?examType=${encodeURIComponent(examType)}&term=${encodeURIComponent(selectedTerm)}&year=${encodeURIComponent(selectedYear)}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

      const raw = Array.isArray(res.data) ? res.data : [];
      // Group by student (year is already filtered by backend)
      const map: Record<string, StudentResult> = {};
      raw.forEach((row: any) => {
        const sid = String(row.studentId || "");
        const sname = row.studentName || "Unknown";
        if (!map[sid]) {
          map[sid] = {
            studentId: sid,
            studentName: sname,
            results: []
          };
        }
        map[sid].results.push({
          subjectId: String(row.subjectId || ""),
          subjectName: row.subjectName || "Unknown",
          score: Number(row.score || 0),
          testTitle: row.testTitle || row.examType || examType,
          testDate: row.testDate || new Date(parseInt(selectedYear), 0, 1).toISOString()
        });
      });

      setResults(Object.values(map));
    } catch (err) {
      console.error("Error fetching results:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedTerm, examType, selectedYear, token]);

  useEffect(() => {
    fetchClasses();
    const classIdFromUrl = searchParams.get("classId");
    if (classIdFromUrl) {
      setSelectedClass(classIdFromUrl);
    }
  }, [fetchClasses, searchParams]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  useEffect(() => {
    if (selectedClass && selectedTerm && examType && selectedYear) {
      fetchResults();
    } else {
      setResults([]);
    }
  }, [selectedClass, selectedTerm, examType, selectedYear, fetchResults]);

  const filteredResults = !searchTerm.trim()
    ? results
    : results.filter(r => r.studentName.toLowerCase().includes(searchTerm.trim().toLowerCase()));
  // Export all results to Excel
  const handleExportAllToExcel = () => {
    if (!results.length || !subjects.length) return;

    const wb = XLSX.utils.book_new();
    const className = classes.find(c => (c.id || c._id) === selectedClass)?.name || "Unknown Class";
    // Data processing with Ranking
    const studentList = results.map(stu => {
      let total = 0;
      let count = 0;
      stu.results.forEach(r => {
        total += r.score;
        count++;
      });
      return {
        ...stu,
        total,
        average: count > 0 ? total / count : 0
      };
    });
    // Sort by average descending
    studentList.sort((a, b) => b.average - a.average);
    // Assign rank
    let rank = 1;
    studentList.forEach((s, idx) => {
      if (idx > 0 && s.average < studentList[idx - 1].average) rank = idx + 1;
      (s as any).rank = rank;
    });

    const rows: any[][] = [
      ["MILESTONE SCHOOL - COMPREHENSIVE ASSESSMENT REGISTRY"],
      [`Class: ${className} | Term: ${selectedTerm} | Assessment: ${examType} | Year: ${selectedYear}`],
      [`Extraction Date: ${new Date().toLocaleDateString()}`],
      [""],
      ["POS", "STUDENT NAME", ...subjects.map(s => getSubjectLabel(s).toUpperCase()), "TOTAL", "AVERAGE", "AWARD", "GRADE"]
    ];

    studentList.forEach(stu => {
      const row: any[] = [(stu as any).rank, stu.studentName.toUpperCase()];

      subjects.forEach(sub => {
        const result = stu.results.find(r => String(r.subjectId) === String(sub.id || sub._id));
        const score = result ? result.score : null;
        row.push(score !== null ? score : "-");
      });

      row.push(formatAssessmentMetric(stu.total));
      row.push(formatAssessmentMetric(stu.average));
      const avg = stu.average;
      row.push(getAwardDisplay(avg));
      row.push(avg >= 80 ? "A" : avg >= 70 ? "B" : avg >= 60 ? "C" : avg >= 50 ? "D" : "F");
      rows.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);

    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const lastCol = range.e.c;
      const lastRow = range.e.r;
      // Dynamic Column Widths
      const cols = [{ wch: 6 }]; // 
      cols.push({ wch: 30 }); // Name
      for (let i = 0; i < subjects.length; i++) cols.push({ wch: 15 }); // 
      cols.push({ wch: 12 }); // Total
      cols.push({ wch: 12 }); // Average
      cols.push({ wch: 10 }); // Award
      cols.push({ wch: 8 }); // Grade
      ws["!cols"] = cols;
      // Merges for Headers
      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } });
      ws["!merges"].push({ s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } });
      ws["!merges"].push({ s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } });
      // Apply Styles
      for (let r = 0; r <= lastRow; r++) {
        for (let c = 0; c <= lastCol; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) continue;

          ws[addr].s = {
            font: { name: "Arial", sz: 10 },
            alignment: { vertical: "center", horizontal: c === 0 ? "left" : "center", wrapText: true },
            border: {
              top: { style: "thin", color: { rgb: "CBD5E1" } },
              bottom: { style: "thin", color: { rgb: "CBD5E1" } },
              left: { style: "thin", color: { rgb: "CBD5E1" } },
              right: { style: "thin", color: { rgb: "CBD5E1" } }
            }
          };

          if (r === 0) {
            ws[addr].s = {
              font: { bold: true, sz: 18, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "1E40AF" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          } else if (r === 1 || r === 2) {
            ws[addr].s = {
              font: { bold: true, sz: 11 },
              fill: { fgColor: { rgb: "F1F5F9" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          } else if (r === 4) {
            ws[addr].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "334155" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          } else if (r > 4) {
            if ((r - 5) % 2 === 0) ws[addr].s.fill = { fgColor: { rgb: "F8FAFC" } };
            // Highlight Grade column
            if (c === lastCol) {
              const grade = ws[addr].v;
              if (grade === "F") ws[addr].s.font.color = { rgb: "EF4444" };
              else if (grade === "A") ws[addr].s.font.color = { rgb: "10B981" };
              ws[addr].s.font.bold = true;
            }
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, "Class Results");
    XLSX.writeFile(wb, `${className}_${selectedTerm}_${examType}_Results.xlsx`);
  };
  // Export single student results to Excel
  const handleExportStudentToExcel = (student: StudentResult) => {
    if (!student.results.length) return;

    const wb = XLSX.utils.book_new();
    const className = classes.find(c => (c.id || c._id) === selectedClass)?.name || "Unknown Class";
    // Calculate rank among all students in the results set
    const studentAverages = results.map(s => {
      let t = 0;
      s.results.forEach(r => t += r.score);
      return { sid: s.studentId, avg: s.results.length > 0 ? t / s.results.length : 0 };
    });
    studentAverages.sort((a, b) => b.avg - a.avg);

    let position = 1;
    for (let i = 0; i < studentAverages.length; i++) {
      if (i > 0 && studentAverages[i].avg < studentAverages[i - 1].avg) position = i + 1;
      if (studentAverages[i].sid === student.studentId) break;
    }

    const rows: any[][] = [
      ["MILESTONE SCHOOL - INDIVIDUAL PERFORMANCE REPORT"],
      [`Student: ${student.studentName.toUpperCase()}`],
      [`Class Position: ${position} of ${results.length}`],
      [`Class: ${className} | Term: ${selectedTerm} | Assessment: ${examType} | Year: ${selectedYear}`],
      [`Date: ${new Date().toLocaleDateString()}`],
      [""],
      ["SUBJECT NAME", "SCORE (%)", "GRADE", "RESULT STATUS"]
    ];

    let total = 0;
    let count = 0;

    student.results.forEach(result => {
      const score = result.score;
      const grade = score >= 80 ? "A" : score >= 70 ? "B" : score >= 60 ? "C" : score >= 50 ? "D" : "F";
      const status = score >= 50 ? "PASSED" : "FAILED";
      rows.push([result.subjectName.toUpperCase(), score, grade, status]);
      total += score;
      count++;
    });

    const average = count > 0 ? total / count : 0;
    const overallGrade = average >= 80 ? "A" : average >= 70 ? "B" : average >= 60 ? "C" : average >= 50 ? "D" : "F";

    rows.push([""]);
    rows.push(["TOTAL", formatAssessmentMetric(total), "", ""]);
    rows.push(["OVERALL AVERAGE", formatAssessmentMetric(average), overallGrade, average >= 50 ? "PASSED" : "FAILED"]);

    const ws = XLSX.utils.aoa_to_sheet(rows);

    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const lastCol = range.e.c;
      const lastRow = range.e.r;

      ws["!cols"] = [{ wch: 35 }, { wch: 15 }, { wch: 12 }, { wch: 20 }];

      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } });
      ws["!merges"].push({ s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } });
      ws["!merges"].push({ s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } });
      ws["!merges"].push({ s: { r: 3, c: 0 }, e: { r: 3, c: lastCol } });
      ws["!merges"].push({ s: { r: 4, c: 0 }, e: { r: 4, c: lastCol } });

      for (let r = 0; r <= lastRow; r++) {
        for (let c = 0; c <= lastCol; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) continue;

          ws[addr].s = {
            font: { name: "Arial", sz: 10 },
            alignment: { vertical: "center", horizontal: c === 0 ? "left" : "center" },
            border: {
              top: { style: "thin", color: { rgb: "CBD5E1" } },
              bottom: { style: "thin", color: { rgb: "CBD5E1" } },
              left: { style: "thin", color: { rgb: "CBD5E1" } },
              right: { style: "thin", color: { rgb: "CBD5E1" } }
            }
          };

          if (r === 0) {
            ws[addr].s = {
              font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "1E40AF" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          } else if (r >= 1 && r <= 4) {
            ws[addr].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "F1F5F9" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
            if (r === 2) ws[addr].s.font.color = { rgb: "1E40AF" };
          } else if (r === 6) {
            ws[addr].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "334155" } }
            };
          } else if (r === lastRow) {
            ws[addr].s.font.bold = true;
            ws[addr].s.fill = { fgColor: { rgb: "E5E7EB" } };
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, "Student Performance");
    XLSX.writeFile(wb, `${student.studentName}_${selectedTerm}_${examType}_Performance.xlsx`);
  };
  // Export all results to PDF
  const handleExportAllToPDF = async () => {
    if (!results.length || !subjects.length || !selectedClass || !token) return;

    try {
      const params = new URLSearchParams({
        classId: selectedClass,
        examType: examType,
        term: selectedTerm,
        year: selectedYear
      });

      const response = await axios.get(`${getApiUrl("export/pdf/assessments")}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${classes.find(c => (c.id || c._id) === selectedClass)?.name || 'Results'}_${selectedTerm}_${examType}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };
  // Export all results to Word
  const handleExportAllToWord = async () => {
    if (!results.length || !subjects.length || !selectedClass || !token) return;

    try {
      const params = new URLSearchParams({
        classId: selectedClass,
        examType: examType,
        term: selectedTerm,
        year: selectedYear
      });

      const response = await axios.get(`${getApiUrl("export/word/assessments")}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${classes.find(c => (c.id || c._id) === selectedClass)?.name || 'Results'}_${selectedTerm}_${examType}_${selectedYear}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting Word:', error);
    }
  };
  // Export single student to PDF
  const handleExportStudentToPDF = async (student: StudentResult) => {
    if (!student.results.length || !selectedClass || !token) return;

    try {
      const params = new URLSearchParams({
        studentId: student.studentId,
        classId: selectedClass,
        examType: examType,
        term: selectedTerm,
        year: selectedYear
      });

      const response = await axios.get(`${getApiUrl("export/pdf/student")}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${student.studentName}_${selectedTerm}_${examType}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };
  // Export single student to Word
  const handleExportStudentToWord = async (student: StudentResult) => {
    if (!student.results.length || !selectedClass || !token) return;

    try {
      const params = new URLSearchParams({
        studentId: student.studentId,
        classId: selectedClass,
        examType: examType,
        term: selectedTerm,
        year: selectedYear
      });

      const response = await axios.get(`${getApiUrl("export/word/student")}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${student.studentName}_${selectedTerm}_${examType}_${selectedYear}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting Word:', error);
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>View Assessment Results</h1>
            <p className="page-subtitle">View and export assessment results by class, year, and assessment type</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExportAllToExcel}
              disabled={!results.length || !subjects.length}
            >
              <FileSpreadsheet size={18} /> Export All to Excel
            </button>
            <button
              className="bg-slate-700 hover:bg-slate-800 text-white flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => window.print()}
              disabled={!results.length || !subjects.length}
            >
              <Printer size={18} /> Print
            </button>
            <button
              className="bg-red-500 hover:bg-red-600 text-white flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExportAllToPDF}
              disabled={!results.length || !subjects.length}
            >
              <Printer size={18} /> Export All to PDF
            </button>
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExportAllToWord}
              disabled={!results.length || !subjects.length}
            >
              <FileType size={18} /> Export All to Word
            </button>
          </div>
        </div>

        <div className="utopia-page-container">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 p-8 border-b border-slate-100 bg-slate-50/20">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Class</label>
              <select
                className="utopia-input h-14 text-sm font-black leading-normal"
                value={selectedClass}
                onChange={e => setSelectedClass(e.target.value)}
              >
                <option value="">Select Class...</option>
                {classes.map(c => (
                  <option key={c.id || c._id} value={c.id || c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Term</label>
              <select
                className="utopia-input h-14 text-sm font-black leading-normal"
                value={selectedTerm}
                onChange={e => setSelectedTerm(e.target.value)}
              >
                <option value="">Select Term...</option>
                {TERM_OPTIONS.map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Assessment Type</label>
              <select
                className="utopia-input h-14 text-sm font-black leading-normal"
                value={examType}
                onChange={e => setExamType(e.target.value)}
              >
                <option value="">Select Assessment Type...</option>
                {assessmentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Year</label>
              <select
                className="utopia-input h-14 text-sm font-black leading-normal"
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value)}
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                className="utopia-btn-primary w-full h-12 shadow-md shadow-teal-50"
                onClick={fetchResults}
                disabled={!selectedClass || !selectedTerm || !examType || !selectedYear || loading}
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin inline mr-2" />
                ) : (
                  <Filter size={18} className="inline mr-2" />
                )}
                {loading ? "Loading..." : "View Results"}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-32 text-center text-slate-300">
              <Loader2 size={48} className="animate-spin mx-auto opacity-10" />
              <p className="text-[10px] font-black uppercase tracking-[0.2em] mt-4">Loading Results...</p>
            </div>
          ) : results.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="flex flex-col gap-3 px-4 pt-4 sm:flex-row sm:items-center">
                <span className="text-xs font-black uppercase tracking-widest text-slate-400">
                  {filteredResults.length} student{filteredResults.length !== 1 ? "s" : ""}
                </span>
                <div className="relative w-full sm:ml-auto sm:max-w-xs">
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="utopia-input h-10 text-xs font-black"
                  />
                </div>
              </div>
              {searchTerm.trim() && filteredResults.length === 0 ? (
                <div className="px-4 py-3 text-sm font-semibold text-amber-700">
                  No student found matching "{searchTerm.trim()}".
                </div>
              ) : null}
              <table className="utopia-table">
                <thead>
                  <tr>
                    <th className="text-center bg-slate-50 font-black w-12">POS</th>
                    <th className="sticky left-0 bg-white z-10 w-64 border-r border-slate-50">Student Name</th>
                    {subjects.map(s => (
                      <th key={s.id || s._id} className="text-center min-w-[120px]">
                        {getSubjectLabel(s)}
                      </th>
                    ))}
                    <th className="text-center bg-slate-50 font-black min-w-[100px]">Total</th>
                    <th className="text-center bg-slate-50 font-black min-w-[100px]">Average</th>
                    <th className="text-center bg-slate-50 font-black min-w-[100px]">Award</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rankedResults = filteredResults.map(stu => {
                      let total = 0;
                      let count = 0;
                      stu.results.forEach(r => { total += r.score; count++; });
                      return { ...stu, total, avg: count > 0 ? total / count : 0 };
                    }).sort((a, b) => b.avg - a.avg);

                    let currentRank = 1;
                    return rankedResults.map((stu, idx) => {
                      if (idx > 0 && stu.avg < rankedResults[idx - 1].avg) currentRank = idx + 1;
                      const isTop = currentRank === 1;

                      return (
                        <tr key={stu.studentId} className={`group/row ${isTop ? 'bg-teal-50/30' : ''}`}>
                          <td className="text-center font-black text-slate-400 text-xs">
                            <span className={isTop ? 'text-teal-600 bg-teal-100 px-2 py-0.5 rounded-md' : ''}>
                              {currentRank}
                            </span>
                          </td>
                          <td className="sticky left-0 bg-white z-10 border-r border-slate-50">
                            <button
                              onClick={() => {
                                setModalStudent({
                                  id: stu.studentId,
                                  name: stu.studentName,
                                  className: classes.find(c => (c.id || c._id) === selectedClass)?.name
                                });
                                setStudentModalOpen(true);
                              }}
                              className="flex items-center gap-3 font-black text-slate-800 uppercase tracking-tight text-xs hover:text-teal-600 transition-all"
                            >
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${isTop ? 'bg-teal-100 text-teal-600' : 'bg-slate-100 text-slate-400'}`}>
                                <User size={14} />
                              </div>
                              {stu.studentName}
                            </button>
                          </td>
                          {subjects.map(sub => {
                            const result = stu.results.find(r => String(r.subjectId) === String(sub.id || sub._id));
                            return (
                              <td key={sub.id || sub._id} className="text-center">
                                {result ? (
                                  <span className={`text-xs font-black px-2 py-1 rounded-lg ${result.score < 50
                                    ? 'text-rose-500 bg-rose-50'
                                    : result.score >= 80
                                      ? 'text-teal-600 bg-teal-50'
                                      : 'text-slate-700'
                                    }`}>
                                    {result.score}
                                  </span>
                                ) : (
                                  <span className="text-slate-200">-</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="text-center bg-slate-50/50">
                            <span className="text-xs font-black px-3 py-1 rounded-full bg-slate-200 text-slate-700">
                              {formatAssessmentMetric(stu.total)}
                            </span>
                          </td>
                          <td className="text-center bg-slate-50/50">
                            <span className={`text-xs font-black px-3 py-1 rounded-full ${stu.avg >= 75
                              ? 'bg-teal-500 text-white'
                              : stu.avg >= 50
                                ? 'bg-slate-200 text-slate-700'
                                : 'bg-rose-100 text-rose-600'
                              }`}>
                              {formatAssessmentMetric(stu.avg)}
                            </span>
                          </td>
                          <td className="text-center bg-slate-50/50">
                            <span className={`text-xs font-black px-3 py-1 rounded-full ${
                              stu.avg >= 80
                                ? 'bg-emerald-100 text-emerald-700'
                                : stu.avg >= 70
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-rose-100 text-rose-700'
                            }`}>
                              {getAwardDisplay(stu.avg)}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          ) : selectedClass && selectedTerm && examType && selectedYear ? (
            <div className="p-32 text-center text-slate-300">
              <LayoutGrid size={80} className="mx-auto opacity-5" />
              <p className="text-sm font-medium mt-4">No results found for the selected filters.</p>
            </div>
          ) : (
            <div className="p-32 text-center text-slate-300">
              <LayoutGrid size={80} className="mx-auto opacity-5" />
              <p className="text-sm font-medium mt-4">Please select Class, Term, Assessment Type, and Year to view results.</p>
            </div>
          )}
        </div>
      </main>

      {studentModalOpen && modalStudent && (
        <StudentPerformanceModal
          isOpen={studentModalOpen}
          onClose={() => { setStudentModalOpen(false); setModalStudent(null); }}
          student={{ id: modalStudent.id, name: modalStudent.name, email: "", className: modalStudent.className }}
        />
      )}
    </div>
  );
};

export default ViewTestResults;
